/**
 * BLRRD paint engine — brush dynamics (Phase 4.C.paint.1, Wave 9.1).
 *
 * Two hardcoded input→parameter mappings, switchable per stroke:
 *
 *   • pressure → size     (PointerEvent.pressure ∈ [0, 1])
 *   • velocity → opacity   (pointer speed in px/ms, inverted: slow = full)
 *
 * GIMP's full dynamics system binds any input to any parameter via a curve
 * editor; that's over-engineered for v1 (dossier §3.2 + trap #4). The two
 * linear mappings below cover ~90% of user intent.
 *
 * Provenance (clean-room): the algorithm is "normalise an input, multiply
 * a parameter by it" — no special math, no source consulted. Pointer
 * Events API per W3C spec. Dossier §3.
 *
 * @module src/pipeline/paint/dynamics
 */

/* Velocity at which opacity reaches its floor. ~2 px/ms feels natural for
 * a desktop mouse drag (dossier §3.1). */
const VELOCITY_REFERENCE = 2.0;

/* Size mapping floor: even at zero pressure the brush keeps 20 % of its
 * base size so a mouse (constant pressure 0.5) never shrinks to nothing. */
const SIZE_FLOOR = 0.2;

/* Opacity mapping ceiling: a maximally fast stroke still keeps 30 % opacity
 * (1 − 0.7) so fast strokes aren't fully transparent. */
const VELOCITY_OPACITY_RANGE = 0.7;

/**
 * Resolve a usable pressure value from a pointer event. Some Bluetooth
 * styli report 0 instead of 0.5 when they have no force sensor (dossier
 * trap #9), so we treat a 0 from a non-mouse pointer as the 0.5 default.
 *
 * @param {object} evt  PointerEvent-like { pressure, pointerType }
 * @returns {number} pressure ∈ [0, 1]
 */
export function resolvePressure(evt) {
    if (!evt) return 0.5;
    let p = Number(evt.pressure);
    if (!Number.isFinite(p)) return 0.5;
    const type = evt.pointerType;
    if (p === 0 && type !== 'mouse') return 0.5;   /* sensor-less stylus */
    if (p < 0) p = 0;
    if (p > 1) p = 1;
    /* A mouse always reports 0.5; pen/touch report real values. */
    return p;
}

/**
 * Compute pointer velocity (px/ms) between two events. Guards a zero/neg
 * time delta (events can share a timestamp on some platforms).
 *
 * @param {number} x0
 * @param {number} y0
 * @param {number} t0  timestamp (ms)
 * @param {number} x1
 * @param {number} y1
 * @param {number} t1
 * @returns {number} normalised velocity ∈ [0, 1]
 */
export function normalizedVelocity(x0, y0, t0, x1, y1, t1) {
    const dx = x1 - x0;
    const dy = y1 - y0;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const dt = Math.max(t1 - t0, 1);   /* clamp to ≥ 1 ms */
    const v = dist / dt;
    const vn = v / VELOCITY_REFERENCE;
    return vn < 0 ? 0 : vn > 1 ? 1 : vn;
}

/**
 * Apply the size dynamic. With pressure-to-size off, returns the base
 * size unchanged.
 *
 * @param {number} baseSize       brush diameter slider value
 * @param {number} pressure       [0, 1]
 * @param {boolean} pressureToSize
 * @returns {number} effective size
 */
export function effectiveSize(baseSize, pressure, pressureToSize) {
    if (!pressureToSize) return baseSize;
    const p = Number.isFinite(pressure) ? Math.max(0, Math.min(1, pressure)) : 0.5;
    const factor = SIZE_FLOOR + (1 - SIZE_FLOOR) * p;   /* 0.2 .. 1.0 */
    return baseSize * factor;
}

/**
 * Apply the opacity dynamic. With velocity-to-opacity off, returns the
 * base opacity unchanged. Faster strokes → lower opacity (inverted).
 *
 * @param {number} baseOpacity       brush opacity slider value [0, 1]
 * @param {number} velocityNorm      [0, 1]
 * @param {boolean} velocityToOpacity
 * @returns {number} effective opacity
 */
export function effectiveOpacity(baseOpacity, velocityNorm, velocityToOpacity) {
    if (!velocityToOpacity) return baseOpacity;
    const vn = Number.isFinite(velocityNorm) ? Math.max(0, Math.min(1, velocityNorm)) : 0;
    const factor = 1 - VELOCITY_OPACITY_RANGE * vn;     /* 1.0 .. 0.3 */
    return baseOpacity * factor;
}

export {
    VELOCITY_REFERENCE, SIZE_FLOOR, VELOCITY_OPACITY_RANGE,
};
